import { call, put, retry, select, takeLatest } from 'redux-saga/effects';
import AuthConstants from '../auth/models/AuthConstants';
import invokeApi from '../network';
import networkConfig from '../network/config';
import { setErrorCode } from '../redux/Actions/ErrorAction';
import { resetStoreData } from '../redux/Actions/StoresAction';
import { getCustomerInfoError, getCustomerInfoSuccess, resetCustomerInfo, } from '../redux/Actions/OrderservAction';
import { resetBraintreeReducer } from '../redux/Actions/BraintreePaymentAction';
import App from '../App';
import { clearTokens, getGoogleTokensFailure, getGoogleTokensSuccess, getTokensFailure, getTokensSuccess, logoutFailure, logoutSuccess, renewAccessTokenFailure, renewAccessTokenSuccess, } from '../redux/Actions/AuthAction';
import { FETCH_CUSTOMER_DETAILS } from '../constants';
import { getTokens } from '../redux/Actions/AuthAction';
import AuthManager from '../auth/AuthManager';
import { setUserParams } from '../redux/Actions/AppStateAction';
import { cartClearReducer } from '../redux/Actions/CartAction';
import { store } from '../redux/Store';
import { resetPostOrder } from '../redux/Actions/PostOrderAction';
import { resetOrderHistory } from '../redux/Actions/OrderHistoryAction';
const { getInstance: auth } = AuthManager;
const { RENEW_ACCESS_TOKEN, REFRESH_TOKEN_RETRY_DELAY_MILLIS, GET_TOKENS, LOGOUT_USER, GET_GOOGLE_SPOT_TOKENS } = AuthConstants;
const FORM_URL_ENCODED_HEADER = {
    'Content-Type': 'application/x-www-form-urlencoded',
};
export function* checkHttpErrors(status, response) {
    if (status !== 200) {
        yield put(setErrorCode(status));
        throw new Error(response);
    }
}
export function* fetchTokens(request) {
    const authManager = auth();
    try {
        const { formData, endpointConfig } = authManager.getRequestConfig(request?.payload);
        authManager.clearIntervalIfExists();
        const { status, response } = yield call(invokeApi, endpointConfig, {}, FORM_URL_ENCODED_HEADER, {}, formData);
        yield* checkHttpErrors(status, response);
        const modifiedResponse = authManager.modifyTokensResponse(response);
        yield put(getTokensSuccess(modifiedResponse));
        authManager.updateCustomerInfo();
        request?.payload?.successCallback?.();
    }
    catch (e) {
        yield put(getTokensFailure());
        authManager.handleSignInFailure(request?.payload);
    }
}
export function* fetchTokensSaga() {
    yield takeLatest(GET_TOKENS, fetchTokens);
}
export const clientIdSelector = ({ authReducer: { response: { clientId } = {} } = {} }) => ({
    clientId,
});
export function* refreshAccessToken(request) {
    const authManager = auth();
    try {
        const { clientId } = yield select(clientIdSelector);
        const requestPayload = {
            clientId,
            grantType: App.envVars?.authParams?.refreshGrantType,
            scope: '',
            refreshToken: authManager.refreshToken,
        };
        const { formData } = authManager.getRequestConfig(requestPayload);
        authManager.clearIntervalIfExists();
        const { response, status } = yield retry(App.envVars?.authParams?.refreshTokenMaxRetryCount, REFRESH_TOKEN_RETRY_DELAY_MILLIS, invokeApi, networkConfig.refreshToken, {}, FORM_URL_ENCODED_HEADER, {}, formData);
        yield* checkHttpErrors(status, response);
        const modifiedResponse = authManager.modifyTokensResponse(response, clientId);
        yield put(renewAccessTokenSuccess(modifiedResponse));
    }
    catch (e) {
        const { payload: { isMemberLogin = false } = {} } = request;
        authManager.clearIntervalIfExists();
        authManager.resetTokens();
        yield put(clearTokens());
        if (!isMemberLogin) {
            authManager.bootstrapAuthentication();
        }
        yield put(renewAccessTokenFailure());
        //TODO @Sara Handle Failure - get new guest token (possible solution)
    }
}
export function* refreshTokenSaga() {
    yield takeLatest(RENEW_ACCESS_TOKEN, refreshAccessToken);
}
export function* logoutUser(actionPayload) {
    const { successCallback } = actionPayload ?? {};
    try {
        const { response, status } = yield call(invokeApi, networkConfig.logout);
        const authManager = auth();
        authManager.clearIntervalIfExists();
        yield* checkHttpErrors(status, response);
        yield put(resetCustomerInfo());
        yield put(resetBraintreeReducer());
        yield put(cartClearReducer());
        yield put(setUserParams({ loggedIn: false, localized: false }));
        yield put(resetStoreData());
        yield put(logoutSuccess());
        yield put(getTokens());
        yield put(resetPostOrder());
        yield put(resetOrderHistory());
        successCallback && successCallback();
    }
    catch (e) {
        yield put(logoutFailure());
    }
}
export function* logoutUserSaga() {
    yield takeLatest(LOGOUT_USER, logoutUser);
}
export function* fetchGoogleSpotTokens(request) {
    try {
        const authManager = auth();
        const payload = {
            token: request?.payload?.token,
            clientId: App.envVars?.authParams?.googleClientId,
            scope: App.envVars?.authParams?.googleAuthScope,
            grantType: App.envVars?.authParams?.googleGrantType,
        };
        const { formData } = authManager.getRequestConfig(payload);
        authManager.clearIntervalIfExists();
        const { status, response } = yield call(invokeApi, networkConfig.validateToken, {}, FORM_URL_ENCODED_HEADER, {}, formData);
        yield* checkHttpErrors(status, response);
        const modifiedResponse = authManager.modifyTokensResponse(response, App.envVars?.authParams?.googleClientId);
        yield put(getGoogleTokensSuccess(modifiedResponse));
        authManager.updateCustomerInfo();
    }
    catch (e) {
        yield put(getGoogleTokensFailure());
    }
}
export function* fetchGoogleSpotTokensSaga() {
    yield takeLatest(GET_GOOGLE_SPOT_TOKENS, fetchGoogleSpotTokens);
}
export const tenantReducerSelector = ({ tenantReducer: { tenantId } = {} }) => ({
    tenantId,
});
export function* fetchCustomerDetails({ userData = {} } = {}) {
    const authManager = auth();
    try {
        const { tenantId } = yield select(tenantReducerSelector);
        const { payload, customerId } = authManager.customerInfoRequestPayload;
        const { profileReducer: { basic } = {} } = store.getState() ?? {};
        let reqPayload = payload;
        if (Object.keys(userData).length && userData?.attributes) {
            reqPayload = userData;
        }
        else if (userData?.updateUserData) {
            const obj = {
                phoneNumber: basic?.phone ?? userData?.phoneNumber,
                firstName: basic?.firstName,
                lastName: basic?.lastName,
                emailId: basic?.email,
                attributes: {
                    birthDay: basic?.birthDay ?? null,
                    birthYear: basic?.birthYear ?? null,
                    birthMonth: basic?.birthMonth ?? null,
                    postalCode: basic?.postalCode ?? null,
                },
            };
            reqPayload = Object.assign({}, reqPayload, obj);
        }
        const { response, status } = yield call(invokeApi, networkConfig.customerInformation, { queryProps: { customerId } }, {
            'x-tenant-id': tenantId,
            'x-is-registered-user': 'true',
            'x-user-id': customerId,
        }, reqPayload);
        yield* checkHttpErrors(status, response);
        yield put(getCustomerInfoSuccess(response));
        authManager.updateBasketWithNewCustomer();
        authManager.updateRecentOrderWithNewCustomer();
    }
    catch (error) {
        yield put(getCustomerInfoError());
        authManager.handleSignInFailure();
    }
}
export function* fetchCustomerDetailsSaga() {
    yield takeLatest(FETCH_CUSTOMER_DETAILS, fetchCustomerDetails);
}
